import React, { FC, useMemo, useState, ReactElement } from 'react';
import { StateChangeOptions } from 'downshift';
import { SelectInput, BaseProps } from '../Select';

import styles from './styles.module.css';

export type ComboBoxProps = BaseProps & {
  variant: 'combobox';
  onChange: (value: string, isOptionListClick?: boolean) => void;
};

export const USER_VALUE_KEY: string = 'useUserEnteredValueKey';

export const ComboBox: FC<ComboBoxProps> = ({
  value,
  variant,
  options,
  onChange,
  onBlur,
  ...props
}) => {
  const [searchString, changeSearchString] = useState<string>('');
  const [hasFocus, changeHasFocus] = useState<boolean>(false);

  const currentFilteredOptions = useMemo(() => {
    const selectedValueInOptions = Object.keys(options).some(option => {
      return option === value;
    });

    const searchStringInOptions = Object.values(options).some(option => {
      return option === searchString;
    });

    /* Build custom value option. Prefer search string; fallback to value */
    const initialOptions =
      (!searchString && (selectedValueInOptions || !value)) ||
      (searchString && searchStringInOptions)
        ? {}
        : {
            [USER_VALUE_KEY]: (
              <span className={styles.useCurrentValueOption}>
                + Use "{searchString || value}"
              </span>
            ),
          };

    if (!searchString) {
      return { ...initialOptions, ...options };
    }

    return Object.entries(options).reduce<{
      [k: string]: string | ReactElement;
    }>((acc, [optionValue, label]) => {
      if (
        searchString &&
        typeof label === 'string' &&
        (label.toLowerCase().includes(searchString.toLowerCase()) ||
          searchString === optionValue)
      ) {
        return {
          ...acc,
          [optionValue]: label,
        };
      }
      return acc;
    }, initialOptions);
  }, [searchString, options, value]);

  return (
    <SelectInput
      {...props}
      variant={variant}
      value={hasFocus ? searchString : value}
      options={currentFilteredOptions}
      onFocus={() => changeHasFocus(true)}
      onChange={(changes: StateChangeOptions<string>) => {
        if (changes.hasOwnProperty('selectedItem') && changes.selectedItem) {
          if (changes.selectedItem === USER_VALUE_KEY) {
            // user entered string and clicks "Use ..." option
            if (searchString) {
              onChange(searchString, true);
            } else if (value) {
              changeSearchString(value);
            }
          } else {
            // user selects a provided option
            changeSearchString(changes.selectedItem);
            if (changes.selectedItem !== value) {
              onChange(changes.selectedItem, true);
            }
          }
        } else if (changes.hasOwnProperty('inputValue')) {
          // type event for user entered string, happens every key stroke
          changeSearchString(changes.inputValue || '');
        }
      }}
      onBlur={event => {
        if (searchString && searchString !== value) {
          // Select item on blur; check for valid option and use if found, otherwise use searchString
          const validOption = Object.keys(options).find(key => {
            const label = options[key];
            return typeof label === 'string' && label === searchString;
          });
          onChange(validOption || searchString, false);
        }
        changeSearchString('');
        changeHasFocus(false);
        onBlur && onBlur(event);
      }}
    />
  );
};
